import type { InputState } from "../input/input";
import { moveCircle, norm } from "./physics";
import { moveMul } from "./weather";
import { Tile, type TileId, type WorldState, PLAYER_RADIUS } from "./types";
import { homeInterior } from "../world/overworldMap";

const HOME = homeInterior();

const ACCEL = 20;
const MAX = 3.45;
const FRICTION = 13;
const TURN = 11;
const DASH_SPD = 8.6;
const DASH_T = 0.18;
const DASH_CD = 0.84;

export function tilesNow(w: WorldState): { tiles: Uint8Array; w: number; h: number } {
  if (w.mode === "hollow" && w.hollow) {
    return { tiles: w.hollow.tiles, w: w.hollow.w, h: w.hollow.h };
  }
  if (w.mode === "home") return { tiles: HOME.tiles, w: HOME.w, h: HOME.h };
  return { tiles: w.overworld.tiles, w: w.overworld.w, h: w.overworld.h };
}

export function tickPlayer(w: WorldState, dt: number, input: InputState): void {
  const p = w.player;
  p.invuln = Math.max(0, p.invuln - dt);
  p.dashCd = Math.max(0, p.dashCd - dt);
  if (p.dashT > 0) p.dashT = Math.max(0, p.dashT - dt);

  const wish = norm(input.ax, input.ay);
  if (wish.x || wish.y) p.facing = wish;

  const inj = 1 - p.injury * 0.28;
  const hung = p.hunger > 0.85 ? 0.82 : 1;
  const max = MAX * inj * hung * moveMul(w) * terrainMul(standingTile(w));
  const map = tilesNow(w);

  if (input.dash && p.dashCd <= 0 && p.stamina > 0.18 && (wish.x || wish.y)) {
    p.dashT = DASH_T;
    p.dashCd = DASH_CD;
    p.stamina -= 0.22;
    p.vx = wish.x * DASH_SPD;
    p.vy = wish.y * DASH_SPD;
  }

  if (p.dashT > 0) {
    const moved = moveCircle(map.tiles, map.w, map.h, p, { x: p.vx, y: p.vy }, dt, PLAYER_RADIUS);
    p.x = moved.x;
    p.y = moved.y;
    p.vx = moved.vx;
    p.vy = moved.vy;
  } else {
    if (wish.x || wish.y) {
      const along = p.vx * wish.x + p.vy * wish.y;
      const lx = wish.x * along;
      const ly = wish.y * along;
      const bleed = Math.exp(-TURN * dt);
      p.vx = lx + (p.vx - lx) * bleed;
      p.vy = ly + (p.vy - ly) * bleed;
      p.vx += wish.x * ACCEL * dt;
      p.vy += wish.y * ACCEL * dt;
    } else {
      const f = Math.exp(-FRICTION * dt);
      p.vx *= f;
      p.vy *= f;
      if (Math.hypot(p.vx, p.vy) < 0.04) {
        p.vx = 0;
        p.vy = 0;
      }
    }
    const sp = Math.hypot(p.vx, p.vy);
    if (sp > max) {
      p.vx = (p.vx / sp) * max;
      p.vy = (p.vy / sp) * max;
    }
    const moved = moveCircle(map.tiles, map.w, map.h, p, { x: p.vx, y: p.vy }, dt, PLAYER_RADIUS);
    p.x = moved.x;
    p.y = moved.y;
    p.vx = moved.vx;
    p.vy = moved.vy;
  }

  const moving = Math.hypot(p.vx, p.vy) > 0.2;
  const starving = p.hunger > 0.72;
  if (moving) p.stamina = Math.max(0, p.stamina - dt * (starving ? 0.07 : 0.04));
  else if (!starving) p.stamina = Math.min(p.staminaMax, p.stamina + dt * 0.12);

  if (w.mode === "hollow") {
    p.hunger = Math.min(1, p.hunger + dt * 0.014);
    if (p.hunger > 0.9) p.hp = Math.max(0, p.hp - dt * 6);
  } else {
    p.hunger = Math.min(1, p.hunger + dt * 0.002);
  }

  if (input.lantern) p.lantern = !p.lantern && p.oil > 0;
  if (p.lantern) {
    p.oil = Math.max(0, p.oil - dt * (w.mode === "hollow" ? 0.018 : 0.01));
    if (p.oil <= 0) p.lantern = false;
  }

  p.injury = Math.max(0, p.injury - dt * 0.002);
}

export function standingOn(w: WorldState): TileId {
  return standingTile(w);
}

function standingTile(w: WorldState): TileId {
  const map = tilesNow(w);
  const t = map.tiles[(w.player.y | 0) * map.w + (w.player.x | 0)];
  return (t ?? Tile.Grass) as TileId;
}

function terrainMul(t: TileId): number {
  if (t === Tile.Path) return 1.06;
  if (t === Tile.Grass || t === Tile.Tall) return 0.9;
  if (t === Tile.Dirt || t === Tile.Soil) return 0.96;
  return 1;
}
